import { useEffect } from 'react'
import { NumberInput, Stack, Text, TextInput } from '@mantine/core'
import type { TaskArgsComponentProps } from '../args-registry'
import {
  buildProposalsClusterArgs,
  DEFAULT_MIN_PATIENTS,
  extractFamilyGroup,
  extractMinPatients,
} from './ProposalsClusterArgs.utils'

/**
 * Args editor/viewer for cpg.proposals.cluster: { minPatients, familyGroup? }. The run
 * groups the pending measure proposals; a cluster backed by minPatients distinct patients
 * (and no related active measure) is recommended. No family group = every group.
 */
export function ProposalsClusterArgs({ value, onChange, readOnly }: TaskArgsComponentProps) {
  const minPatients = extractMinPatients(value)
  const familyGroup = extractFamilyGroup(value)
  const groupText = typeof familyGroup === 'string' ? familyGroup : ''

  // Edit mode opened with no args: seed the default so the dialog opens valid and the
  // posted run carries explicit args.
  useEffect(() => {
    if (!readOnly && (value === undefined || value === null)) {
      onChange?.({ minPatients: DEFAULT_MIN_PATIENTS })
    }
  }, [readOnly, value, onChange])

  if (readOnly) {
    return (
      <Text size="sm">
        Recommend at <b>{typeof minPatients === 'number' ? minPatients : DEFAULT_MIN_PATIENTS}</b>{' '}
        distinct patients ·{' '}
        {groupText ? (
          <>
            family group <b>{groupText}</b>
          </>
        ) : (
          <b>every family group</b>
        )}
      </Text>
    )
  }

  return (
    <Stack gap="sm">
      <NumberInput
        label="Minimum patients"
        description="Distinct patients a cluster needs before it is recommended as a new measure."
        min={1}
        allowDecimal={false}
        value={typeof minPatients === 'number' ? minPatients : ''}
        onChange={(next) => onChange?.(buildProposalsClusterArgs(next, groupText))}
      />
      <TextInput
        label="Family group"
        description="A family group slug (e.g. chronic-disease) to cluster only that group's proposals. Leave empty for every group."
        placeholder="every family group"
        value={groupText}
        onChange={(event) =>
          onChange?.(
            buildProposalsClusterArgs(
              typeof minPatients === 'number' ? minPatients : '',
              event.currentTarget.value,
            ),
          )
        }
      />
    </Stack>
  )
}
